import { useRef } from 'react';
import { EMOTIONS, EMOTION_META, EMOTION_ROWS, NOT_SURE, NOT_SURE_META } from '../utils/emotions';

// Keyboard order: 'Not sure' sits at the head, matching where it renders, so
// ArrowLeft from Joy wraps onto it instead of dead-ending.
const ORDER = [NOT_SURE, ...EMOTIONS];
const META = { ...EMOTION_META, [NOT_SURE]: NOT_SURE_META };

const SAGE = '#7C9A82';

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

/**
 * Emotion chip cloud used at three surfaces:
 *   1. Echo idle screen — "How are you right now?" (required)
 *   2. Echo review screen — "How did that land?" (required)
 *   3. EchoCard retro edit — inline expansion (optional)
 *
 * Single-select and sticky. Tapping the currently selected chip is a no-op
 * at the value level (still fires onChange with the same code so parents like
 * MoodChip can close a popover on any tap). To change the emotion the user
 * must tap a different chip. Rationale: a metadata retro-edit should never
 * "clear" state accidentally — clearing would re-queue the embedding pipeline
 * downstream and the user has already committed to a feeling.
 *
 * Props:
 *   value      — one of EMOTIONS, NOT_SURE, or null
 *   onChange   — (code) => void; always called with a code, never null
 *   size       — 'sm' | 'md' (default 'md')
 *   ariaLabel  — labels the radiogroup (defaults to 'Emotion')
 *   disabled   — visually + interactively locks the picker
 *   className  — passthrough for the outer container
 */
export default function EmotionChipPicker({
  value = null,
  onChange,
  size = 'md',
  ariaLabel = 'Emotion',
  disabled = false,
  className = '',
}) {
  const chipCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const refs = useRef({});

  const handleSelect = (code) => {
    if (disabled) return;
    if (typeof onChange !== 'function') return;
    // Sticky: always send the tapped code (never null). Parents rely on this
    // firing to close popovers; MoodChip short-circuits when code === value
    // so no PATCH goes out for a same-chip tap.
    onChange(code);
  };

  // Arrow keys walk the flat EMOTIONS order (not the row grouping) so left/right
  // never dead-ends at a row edge. Home/End jump to the first/last chip.
  const handleKeyDown = (e, code) => {
    const idx = ORDER.indexOf(code);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      next = ORDER[(idx + 1) % ORDER.length];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      next = ORDER[(idx - 1 + ORDER.length) % ORDER.length];
    } else if (e.key === 'Home') {
      next = ORDER[0];
    } else if (e.key === 'End') {
      next = ORDER[ORDER.length - 1];
    }
    if (next) {
      e.preventDefault();
      refs.current[next]?.focus();
    }
  };

  // Default focus target: the selected chip if there is one, otherwise the
  // very first chip (Joy). Every other chip is tabIndex=-1 (radiogroup pattern).
  const initialFocusCode = value || EMOTIONS[0];

  // One chip, one place. Used by both the 'Not sure' row and the palette.
  const chip = (code) => {
    const meta = META[code];
    const isSelected = value === code;
    const otherSelected = value && !isSelected;
    return (
      <button
        key={code}
        ref={(el) => { refs.current[code] = el; }}
        type="button"
        role="radio"
        aria-checked={isSelected}
        aria-label={meta.label}
        title={meta.label}
        tabIndex={code === initialFocusCode ? 0 : -1}
        onClick={() => handleSelect(code)}
        onKeyDown={(e) => handleKeyDown(e, code)}
        className={`${chipCls} rounded-full font-medium select-none transition-all duration-300 ease-out active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
          isSelected ? 'scale-[1.03] shadow-sm motion-reduce:scale-100' : ''
        } ${otherSelected ? 'opacity-55 hover:opacity-90' : 'opacity-100'}`}
        style={{
          backgroundColor: meta.bg,
          color: meta.fg,
          boxShadow: isSelected
            ? `0 0 0 2px ${SAGE}, 0 1px 3px rgba(45,41,38,0.10)`
            : undefined,
          '--tw-ring-color': SAGE,
        }}
      >
        {meta.label}
      </button>
    );
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={`flex flex-col items-center gap-2.5 max-w-md mx-auto ${
        disabled ? 'opacity-60 pointer-events-none' : ''
      } ${className}`}
    >
      {/* 'Not sure' first, then a hairline. It is an answer, not a feeling, so
          it gets muted stone and its own space above the palette rather than a
          slot inside it. */}
      <div className="flex flex-col items-center gap-2.5 w-full">
        {chip(NOT_SURE)}
        <div className="h-px w-16 bg-stone-200 dark:bg-stone-700" aria-hidden="true" />
      </div>

      {EMOTION_ROWS.map((row, ri) => (
        <div
          key={`row-${ri}`}
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5"
        >
          {row.map((code) => chip(code))}
        </div>
      ))}
    </div>
  );
}
